import { useMemo, useState, useCallback, useRef } from 'react'
import { useQuery } from '@apollo/client'
import { TOPOLOGY_BY_SITE } from './operations-gql/queries'
import { transformTopologyToTree, flattenTopologyEquipments, normalizeMac } from './utils'
import { EQUIPMENT_TYPES } from './constants'

const useTopology = (sfId, hotspotId) => {
  const treeRef = useRef(null)
  const [selectedEquipment, setSelectedEquipment] = useState(null)
  const [hoveredEquipment, setHoveredEquipment] = useState(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [hiddenTypes, setHiddenTypes] = useState(new Set())

  const { data, loading, error } = useQuery(TOPOLOGY_BY_SITE, {
    variables: { sf_id: sfId, hotspot_id: hotspotId || undefined },
    skip: !sfId,
  })

  const network = data?.topology

  const equipments = useMemo(() => network ? flattenTopologyEquipments(network) : [], [network])

  const existingTypes = useMemo(() => {
    if (!network?.nodes) return []
    const otherBase = EQUIPMENT_TYPES.find(eq => eq.value === 'other')
    const seenTypes = new Set(network.nodes.map(n => n.type || 'other'))
    const toEntry = (type) =>
      EQUIPMENT_TYPES.find(eq => eq.value === type) ?? {
        icon: otherBase.icon,
        color: otherBase.color,
        value: type,
        label: type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' '),
      }
    return [
      ...EQUIPMENT_TYPES.map(eq => eq.value).filter(v => seenTypes.has(v)),
      ...[...seenTypes].filter(t => !EQUIPMENT_TYPES.some(eq => eq.value === t)).sort(),
    ].map(toEntry)
  }, [network])

  const toggleType = useCallback((type) => {
    setHiddenTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }, [])

  const [revealedParentMacs, setRevealedParentMacs] = useState(new Set())

  const treeData = useMemo(
    () => network ? transformTopologyToTree(network, hiddenTypes, revealedParentMacs) : null,
    [network, hiddenTypes, revealedParentMacs]
  )

  const handleSelectNode = useCallback((nodeDatum) => {
    const equipment = equipments.find(e => e.mac_address === nodeDatum.mac_address) || nodeDatum
    setSelectedEquipment(prev => prev?.mac_address === nodeDatum.mac_address ? null : equipment)
  }, [equipments])

  const handleCloseDetail = useCallback(() => setSelectedEquipment(null), [])

  const handleHoverNode = useCallback((nodeDatum) => {
    const equipment = equipments.find(e => e.mac_address === nodeDatum.mac_address) || nodeDatum
    setHoveredEquipment(equipment)
  }, [equipments])

  const handleUnhoverNode = useCallback(() => {
    setHoveredEquipment(null)
  }, [])

  const handleSelectFromSidebar = useCallback((mac) => {
    setSelectedEquipment(equipments.find(e => e.mac_address === mac) || null)
    treeRef.current?.focusNode(mac)
  }, [equipments])

  const focusEquipmentByMac = useCallback((mac) => {
    if (!mac) return
    const normalizedMac = normalizeMac(mac)
    const equipment = equipments.find(e => normalizeMac(e.mac_address) === normalizedMac)
    if (!equipment) return
    setSelectedEquipment(equipment)
  }, [equipments])

  const toggleExpand = useCallback(() => setIsExpanded(prev => !prev), [])

  const revealChildrenOf = useCallback((mac) => {
    if (!mac) return
    setRevealedParentMacs(prev => new Set([...prev, mac]))
  }, [])

  const hideRevealedChildrenOf = useCallback((mac) => {
    if (!mac) return
    setRevealedParentMacs(prev => {
      const next = new Set(prev)
      next.delete(mac)
      return next
    })
  }, [])

  const getHiddenChildren = useCallback((mac) => {
    if (!mac || !network?.connections || !hiddenTypes.size) return []
    const childMacs = new Set(
      network.connections.filter(c => c.parent_mac === mac).map(c => c.device_mac)
    )
    return equipments.filter(e => childMacs.has(e.mac_address) && hiddenTypes.has(e.type || 'other'))
  }, [network, equipments, hiddenTypes])

  return {
    treeRef,
    treeData,
    equipments,
    existingTypes,
    hiddenTypes,
    toggleType,
    loading,
    error,
    selectedEquipment,
    hoveredEquipment,
    isExpanded,
    handleSelectNode,
    handleCloseDetail,
    handleHoverNode,
    handleUnhoverNode,
    handleSelectFromSidebar,
    focusEquipmentByMac,
    toggleExpand,
    getHiddenChildren,
    revealChildrenOf,
    hideRevealedChildrenOf,
    revealedParentMacs,
  }
}

export default useTopology
